import { IRestSuccess } from '~/src/dash/api/commons/commons_models';
import {
  IMaxMinMonitorValue,
  IMonitor,
  IMonitorAlertHistory,
  IMonitorCustomMetric,
  IMonitorNotificationHistory,
  IMonitorNotificationSettings,
  transformMonitorCustomMetric,
} from '~/src/dash/api/monitors/monitors_models';
import {
  addDomainAndTokenToUrl,
  createUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';

export async function getAllMonitors(
  publisherID: string,
): Promise<[IMonitor[] | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitors', publisherID);

  try {
    return [(await restGet<IMonitor[]>(reqUrl)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function getMonitor(
  monitorID: string,
  publisherID: string,
): Promise<[IMonitor | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitor/' + monitorID, publisherID);

  try {
    return [(await restGet<IMonitor>(reqUrl)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function createMonitor(
  monitor: IMonitor,
  publisherID: string,
): Promise<[IMonitor | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitor', publisherID);

  try {
    return [(await restPost<IMonitor>(reqUrl, monitor)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function cloneMonitor(
  monitorID: string,
  publisherID: string,
): Promise<[number | undefined, string | undefined]> {
  const reqUrl = createUrl(
    '/publishers/' + publisherID + '/clone_monitor/' + monitorID,
    publisherID,
  );

  try {
    return [(await restPost<number>(reqUrl, undefined)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function updateMonitor(
  monitor: IMonitor,
  publisherID: string,
): Promise<[IMonitor | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitor', publisherID);

  try {
    return [(await restPatch<IMonitor>(reqUrl, monitor)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function deleteMonitor(
  monitorID: string,
  publisherID: string,
): Promise<[null | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitor/' + monitorID, publisherID);

  try {
    return [(await restDelete<null>(reqUrl, null)).parsedBody, undefined];
  } catch (e) {
    return [null, e];
  }
}

export async function getMonitorMinMaxValue(
  monitor: IMonitor,
  publisherID: string,
): Promise<[IMaxMinMonitorValue | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitor/max_min_value', publisherID);

  try {
    return [(await restPost<IMaxMinMonitorValue>(reqUrl, monitor)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function getMonitorMinMaxValueByPublisherIDs(
  monitor: IMonitor,
  publisherIDs: string,
): Promise<[IMaxMinMonitorValue | undefined, string | undefined]> {
  const reqUrl = createUrl(
    '/publishers/' + publisherIDs + '/monitor/max_min_value_by_publisher_ids',
  );

  try {
    return [(await restPost<IMaxMinMonitorValue>(reqUrl, monitor)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function sendTestNotification(
  monitorID: string,
  publisherID: string,
  notification: IMonitorNotificationSettings,
): Promise<[IRestSuccess | undefined, string | undefined]> {
  const reqUrl = createUrl(
    '/publishers/' + publisherID + '/monitor/' + monitorID + '/test_notification',
    publisherID,
  );

  try {
    return [(await restPost<IRestSuccess>(reqUrl, notification)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function getNotificationHistoryForAlert(
  monitorID: number,
  alertID: number,
  publisherID: string,
): Promise<[IMonitorNotificationHistory[] | undefined, string | undefined]> {
  const reqUrl = createUrl(
    '/publishers/' + publisherID + '/monitor/' + monitorID + '/alert/' + alertID + '/notifications',
    publisherID,
  );

  try {
    return [(await restGet<IMonitorNotificationHistory[]>(reqUrl)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function bulkUpdateMonitors(
  monitorIDs: string,
  publisherID: string,
  updates: { [key: string]: any },
  keys: string,
): Promise<any> {
  const url =
    addDomainAndTokenToUrl(`/publishers/${publisherID}/bulk-update-monitors`) +
    `&monitor_ids=${monitorIDs}&keys=${keys}`;
  try {
    return [(await restPatch(url, updates)).parsedBody, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

// Monitor Custom Metrics CRUD
export async function getMonitorCustomMetricsByPublisherID(
  publisherID: string,
): Promise<[IMonitorCustomMetric[] | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitor_custom_metrics', publisherID);

  try {
    return await restGet<IMonitorCustomMetric[]>(reqUrl).then((val: IHttpResponse<any>) => {
      const MonitorCustomMetrics = [] as IMonitorCustomMetric[];
      if (val.parsedBody) {
        for (const MonitorCustomMetric of val.parsedBody) {
          const parsedMonitorCustomMetric = transformMonitorCustomMetric(MonitorCustomMetric);
          MonitorCustomMetrics.push(parsedMonitorCustomMetric);
        }
      }
      return [MonitorCustomMetrics, undefined];
    });
  } catch (e) {
    return [undefined, e];
  }
}

export async function createMonitorCustomMetric(
  monitorCustomMetric: IMonitorCustomMetric,
  publisherID: string,
): Promise<[IMonitorCustomMetric | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitor_custom_metric', publisherID);
  const parsedMonitorCustomMetric = transformMonitorCustomMetric(monitorCustomMetric);

  try {
    const ret = (await restPost<IMonitorCustomMetric>(reqUrl, parsedMonitorCustomMetric))
      .parsedBody;
    return [transformMonitorCustomMetric(ret), undefined];
  } catch (e) {
    return [undefined, e];
  }
}

export async function updateMonitorCustomMetric(
  monitorCustomMetric: IMonitorCustomMetric,
  publisherID: string,
): Promise<[IMonitorCustomMetric | undefined, string | undefined]> {
  const reqUrl = createUrl('/publishers/' + publisherID + '/monitor_custom_metric', publisherID);
  const parsedMonitorCustomMetric = transformMonitorCustomMetric(monitorCustomMetric);
  try {
    return await restPatch<IMonitorCustomMetric>(reqUrl, parsedMonitorCustomMetric).then(
      (val: IHttpResponse<IMonitorCustomMetric>) => {
        return [transformMonitorCustomMetric(val.parsedBody), undefined];
      },
    );
  } catch (e) {
    return [undefined, e];
  }
}

export async function deleteMonitorCustomMetric(
  monitorCustomMetricID: string,
  publisherID: string,
): Promise<[null | undefined, string | undefined]> {
  const reqUrl = createUrl(
    '/publishers/' + publisherID + '/monitor_custom_metric/' + monitorCustomMetricID,
    publisherID,
  );
  try {
    return [(await restDelete<null>(reqUrl, null)).parsedBody, undefined];
  } catch (e) {
    return [null, e];
  }
}

export async function acknowledgeMonitorAlerts(
  monitorID: string,
  alertIDs: string[],
  publisherID: string,
  acknowledgeAlerts: boolean,
): Promise<[IMonitorAlertHistory[] | undefined, string]> {
  const reqUrl =
    createUrl('/publishers/' + publisherID + '/monitor/' + monitorID + '/alert', publisherID) +
    '&alert_ids=' +
    alertIDs.join(',') +
    `&acknowledge_alerts=${acknowledgeAlerts ? 'true' : 'false'}`;

  try {
    return [(await restGet<IMonitorAlertHistory[]>(reqUrl)).parsedBody, ''];
  } catch (e) {
    return [[], e];
  }
}
